import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, LogIn, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function BrandedLogin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login, isAuthenticated, user, loading: authLoading } = useAuth();
  
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch brand data on mount
  useEffect(() => {
    const fetchBrand = async () => {
      try {
        const response = await axios.get(`${API}/whitelabel/brand/${slug}`);
        setBrand(response.data);
      } catch (err) {
        console.error('Brand not found:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchBrand();
    }
  }, [slug]);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await login(formData.email, formData.password);
      toast.success(`Welcome to ${brand?.organization_name || 'the platform'}!`);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || error.message || 'Login failed';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#1a1a2e' }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  // Brand not found - redirect to default login
  if (notFound) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: '#1a1a2e' }}
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Organization Not Found</h1>
          <p className="text-gray-400 mb-6">The branded login page you're looking for doesn't exist.</p>
          <Link 
            to="/login"
            className="px-6 py-3 rounded-lg bg-[#02b5ff] text-white font-semibold hover:bg-[#0099dd] transition-colors"
          >
            Go to Default Login
          </Link>
        </div>
      </div>
    );
  }

  // Dynamic styles based on brand
  const primaryColor = brand?.primary_color || '#02b5ff';
  const secondaryColor = brand?.secondary_color || '#1a1a2e';
  const accentColor = brand?.accent_color || '#ff025f';

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: secondaryColor }}
      data-testid="branded-login-page"
    >
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          {brand?.logo_url ? (
            <img 
              src={`${API}${brand.logo_url}`} 
              alt={brand.organization_name}
              className="h-20 mx-auto mb-4 object-contain"
            />
          ) : (
            <div 
              className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold"
              style={{ backgroundColor: primaryColor }}
            >
              {brand?.organization_name?.charAt(0) || 'P'}
            </div>
          )}
          
          <h1 className="text-2xl font-bold text-white">
            {brand?.organization_name}
          </h1>
          
          {brand?.welcome_message ? (
            <p className="text-gray-400 mt-2">{brand.welcome_message}</p>
          ) : (
            <p className="text-gray-400 mt-2">Sign in to your account</p>
          )}
        </div>

        {/* Login Form */}
        <div 
          className="rounded-2xl shadow-xl p-8 border"
          style={{ 
            backgroundColor: `${secondaryColor}cc`, 
            borderColor: `${primaryColor}30` 
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="branded-login-form">
            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Email Address
              </Label>
              <div className="relative">
                <Mail 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" 
                  style={{ color: `${primaryColor}80` }}
                />
                <Input
                  data-testid="branded-login-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12 pl-10 bg-black/30 border-zinc-700 text-white rounded-xl"
                  style={{ 
                    borderColor: `${primaryColor}40`,
                  }}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Lock 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5"
                  style={{ color: `${primaryColor}80` }}
                />
                <Input
                  data-testid="branded-login-password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 pl-10 pr-12 bg-black/30 border-zinc-700 text-white rounded-xl"
                  style={{ borderColor: `${primaryColor}40` }}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="branded-login-submit"
              type="submit"
              disabled={submitting}
              className="w-full h-12 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
              style={{ 
                backgroundColor: primaryColor,
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <LogIn className="mr-2 h-5 w-5" />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          {/* Partner credit */}
          {brand?.owner_name && (
            <p className="text-center text-gray-500 text-sm mt-6">
              Managed by {brand.owner_name}
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-8">
          Powered by <span className="text-gray-400">BE ELITE APP</span>
        </p>
      </div>
    </div>
  );
}
